import '@/components/progress-bar/';
import type * as ClassTypes from './types/class';
import {
    initCallbackPopup,
    initForm,
    initHeader,
    initPopup,
    initSwiperSlider,
    initToggles,
    lazyLoadElements
} from './common';
import type {IDynamicImport} from './types/utils';
import initFancyBox from '@/components/fancybox/fancybox';
import type {IObserver} from '@/common/scripts/types/observer';
import Observer from './observer';
import type {SwiperOptions} from 'swiper/types/swiper-options';

const observer: IObserver = new Observer();

export const simpleSwiperOptions = (key: string): SwiperOptions => {
    return {
        init      : false,
        navigation: {
            nextEl: `.j-${key}-arrow-next`,
            prevEl: `.j-${key}-arrow-prev`
        },
        pagination: {
            el               : `.j-${key}-pagination`,
            type             : 'bullets',
            clickable        : true,
            bulletClass      : 'swiper-default__pagination-bullet',
            bulletActiveClass: 'is-active'
        },
        slidesPerView: 1,
        spaceBetween : 0
    };
};

// Поведение шапки
const headerWrap = document.querySelector('.j-header') as HTMLElement;

if (headerWrap) {
    initHeader(headerWrap);
}

/* Ленивая загрузка */
/* подключать после инициализации слайдеров */
const lazyElements = [...document.querySelectorAll('[loading=lazy]')];

if (lazyElements.length) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            lazyLoadElements(lazyElements);
        });
    } else {
        lazyLoadElements(lazyElements);
    }
}


// Формы
const formWrappers = [...document.querySelectorAll('.j-form')];

formWrappers.forEach((wrapper: Element) => {
    initForm(wrapper as HTMLElement);
});

// Попапы
const popupSelectors = [...document.querySelectorAll('.j-popup')] as HTMLElement[];

if (popupSelectors.length) {
    initPopup(popupSelectors, {removeDelay: 300});
}

// Отдельный селектор для попапов обратных звонков
const popupFacadeLinks = [...document.querySelectorAll('.j-popup-callback:not([data-init])')] as HTMLElement[];

if (popupFacadeLinks.length) {
    initCallbackPopup(popupFacadeLinks);

    popupFacadeLinks.forEach((button: HTMLElement) => {
        button.dataset['init'] = 'true';
    });
}

// Уведомление об использовании cookies
// const cookiesWrapper = document.querySelector('.j-cookies-notice') as HTMLElement;
//
// if (cookiesWrapper) {
//     import(/* webpackChunkName: "cookies-notice" */ '@/components/cookies-notice')
//         .then(({default: CookiesNotice}: IDynamicImport) => {
//             const cookiesNotice: ClassTypes.ICookiesNotice = new CookiesNotice();
//
//             cookiesNotice.init(cookiesWrapper);
//         });
// }

const setVH = (): void => {
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    let vh = window.innerHeight * 0.01;

    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    // We listen to the resize event
    window.addEventListener('resize', () => {
    // We execute the same script as before
        vh = window.innerHeight * 0.01;

        document.documentElement.style.setProperty('--vh', `${vh}px`);
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        setVH();
    });
} else {
    setVH();
}

const location = document.querySelector('.j-location-map') as HTMLElement;

if (location) {
    import(/* webpackChunkName: "location-section" */ '@/sections/location')
        .then(({default: initLocation}: IDynamicImport) => {
            initLocation(location);
        });
}

const contactsMap = document.querySelector('.j-contacts-map') as HTMLElement;

if (contactsMap) {
    import(/* webpackChunkName: "contacts-section" */ '@/sections/contacts')
        .then(({default: initContactsMap}: IDynamicImport) => {
            initContactsMap(contactsMap);
        });
}


const defaultSliders = [...document.querySelectorAll<HTMLElement>('.j-default-slider')];

if (defaultSliders.length) {
    import(/* webpackChunkName: "defaultSlider" */ '@/sections/slider')
        .then(({default: DefaultSlider}: IDynamicImport) => {
            defaultSliders.forEach((slider: HTMLElement) => {
                new DefaultSlider(slider).init();
            });
        });
}

const customSliders = [...document.querySelectorAll<HTMLElement>('.j-custom-slider')];

if (customSliders.length) {
    import(/* webpackChunkName: "customSlider" */ '@/sections/slider-custom')
        .then(({default: CustomSlider}: IDynamicImport) => {
            customSliders.forEach((slider: HTMLElement) => {
                new CustomSlider(slider).init();
            });
        });
}

const buyConditionsSliders = [...document.querySelectorAll<HTMLElement>('.j-buy-conditions-slider')];

if (buyConditionsSliders.length) {
    import(/* webpackChunkName: "buyConditionsSlider" */ '@/sections/buy-conditions')
        .then(({default: BuyConditionsSlider}: IDynamicImport) => {
            buyConditionsSliders.forEach((slider: HTMLElement) => {
                new BuyConditionsSlider(slider).init();
            });
        });
}

const infoSliders = [...document.querySelectorAll<HTMLElement>('.j-info-slider')];

infoSliders.forEach((infoSlider: HTMLElement) => {
    const sliderKey = infoSlider.dataset['key'] || `swiper__default-${Math.ceil(Math.random() * 100)}`;
    const options = simpleSwiperOptions(sliderKey);

    initSwiperSlider(infoSlider, options);
});

const imagesSliders = [...document.querySelectorAll<HTMLElement>('.j-images-slider')];

imagesSliders.forEach((imagesSlider: HTMLElement) => {
    if (!imagesSlider.dataset['init']) {
        return;
    }

    const sliderKey = imagesSlider.dataset['key'] || `swiper__default-${Math.ceil(Math.random() * 100)}`;
    const options = simpleSwiperOptions(sliderKey);

    initSwiperSlider(imagesSlider, options);
});

const progressWrapper = document.querySelector<HTMLElement>('.j-progress');

if (progressWrapper) {
    import(/* webpackChunkName: "progress" */ '@/sections/progress').then(({default: Progress}: IDynamicImport) => {
        new Progress(progressWrapper).init();
    });
}

const unitsWrapper = document.querySelector<HTMLElement>('.j-units-wrapper');

if (unitsWrapper) {
    import(/* webpackChunkName: "units" */ '@/sections/units')
        .then(({default: Units}: IDynamicImport) => {
            new Units(unitsWrapper).init();
        });
}

const startSlider = document.querySelector<HTMLElement>('.j-start-slider');

if (startSlider) {
    import(/* webpackChunkName: "Start" */ '@/sections/first-screen')
        .then(({default: Start}: IDynamicImport) => {
            new Start(startSlider).init();
        });
}

const switchableContentWrappers = [...document.querySelectorAll<HTMLElement>('.j-switchable-content')];

if (switchableContentWrappers.length) {
    import(/* webpackChunkName: "switchable-content" */ '@/components/switchable-content')
        .then(({default: SwitchableContent}: IDynamicImport) => {
            switchableContentWrappers.forEach((wrapper: HTMLElement) => {
                new SwitchableContent(wrapper).init();
            });
        });
}

// plug-slider
// todo: удалить когда не нужна будет заглуша
const initPlugSlider = (): void => {
    const plugSlider = document.querySelector('.j-slider-plug') as HTMLElement;

    if (plugSlider) {
        import(/* webpackChunkName: "inisSliderPlug" */ '@/components/plug')
            .then(({default: inisSliderPlug}: IDynamicImport) => {
                inisSliderPlug(plugSlider);
            });
    }
};

observer.subscribe('popup:open', (popup: ClassTypes.IPopup) => {
    if (!popup.content) {
        return;
    }

    initPlugSlider();

    // Попапы
    const popups = [...popup.content.querySelectorAll('.j-popup-callback')] as HTMLElement[];

    if (popups.length) {
        initCallbackPopup(popups);

        popups.forEach((button: HTMLElement) => {
            button.dataset['init'] = 'true';
        });
    }
});

// Простые анимации
const animationsBlocks = [...document.querySelectorAll('.j-animation-block')];

if (animationsBlocks.length) {
    if ('IntersectionObserver' in window) {
        const animationObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                if (entry.isIntersecting) {
                    if (!entry.target.classList.contains('is-active')) {
                        entry.target.classList.add('is-active');
                    }
                } else if (entry.target.classList.contains('is-active')) {
                    setTimeout(() => {
                        entry.target.classList.remove('is-active');
                    }, 500);
                }
            });
        });

        animationsBlocks.forEach((block: Element) => {
            animationObserver.observe(block);
        });
    } else {
        animationsBlocks.forEach((block: Element) => {
            block.classList.add('is-active');
        });
    }
}

// Cкролл по якорям
const anchors = [...document.querySelectorAll('.j-mobile-menu-items')] as HTMLElement[];

if (anchors.length) {
    import(/* webpackChunkName: "scrollBy" */ '@/components/scrollBy')
        .then(({default: ScrollBy}: IDynamicImport) => {
            const scrollBy: ClassTypes.IScrollBy = new ScrollBy();
            const CLASS_ACTIVE = 'is-active';

            scrollBy.init({
                offset      : 40,
                takeElements: [headerWrap],
                anchors
            });

            const setActiveAnchor = (id: string): void => {
                anchors.forEach((item: HTMLElement) => {
                    const href = item.getAttribute('href');

                    item.classList.remove(CLASS_ACTIVE);

                    if (href === id || href === `/${id}`) {
                        item.classList.add(CLASS_ACTIVE);
                    }
                });
            };

            const hash = window.location.hash;

            if (hash.length) {
                setActiveAnchor(hash);
            }

            observer.subscribe('anchorChangeSection', (id: string) => {
                setActiveAnchor(id);
            });
        });
}

// Инициализация зума картинок
initFancyBox();

// Туглеры
const toggleWrappers = Array.from(document.querySelectorAll('.j-toggle-wrapper')) as HTMLElement[];

if (toggleWrappers.length) {
    initToggles({items: toggleWrappers});
}
